"use client";

import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaTools,
  FaUser,
  FaCreditCard,
  FaCalculator,
  FaDownload,
  FaPrint,
  FaCalendar,
  FaMoneyBillWave,
  FaUniversity,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import axios from "axios";

const ServicesInvoice = () => {
  const [activeViewTab, setActiveViewTab] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Cheque management state
  const [chequeStatusModal, setChequeStatusModal] = useState({
    show: false,
    chequeId: null,
    status: null,
    notes: "",
  });

  const [rescheduleModal, setRescheduleModal] = useState({
    show: false,
    chequeId: null,
    oldChequeNumber: "",
    newChequeNumber: "",
    newChequeDate: "",
    newBankName: "",
    newBankBranch: "",
    notes: "",
  });

  // Installment management state
  const [installmentPaymentModal, setInstallmentPaymentModal] = useState({
    show: false,
    installmentId: null,
    amount: 0,
    paymentInfo: {
      method: "cash",
      asset_type: "cash",
      asset_id: "",
      amount: 0,
      // Payment method details
      cheque_number: "",
      cheque_date: "",
      bank_name: "",
      bank_branch: "",
      card_transaction_id: "",
      card_bank_name: "",
      card_holder_name: "",
      from_bank_details: "",
      to_bank_details: "",
      transaction_reference: "",
    },
  });

  const [formData, setFormData] = useState({
    invoice_type: "sale",
    customer_id: "",
    customer_contact_id: "",
    title: "",
    reason: "",
    discount: 0,
    tax_id: "",
    items: [],
    extra_charges: [],
    payment_info: {
      method: "cash",
      asset_type: "cash",
      asset_id: "",
      amount: 0,
      immediate_payment: false,
      installments: [],
      // Payment method details
      cheque_number: "",
      cheque_date: "",
      bank_name: "",
      bank_branch: "",
      card_transaction_id: "",
      card_bank_name: "",
      card_holder_name: "",
      from_bank_details: "",
      to_bank_details: "",
      transaction_reference: "",
    },
  });

  // Fetch data based on active tab
  useEffect(() => {
    if (activeViewTab === "list") {
      fetchInvoices();
    } else if (activeViewTab === "create") {
      fetchBasicData();
      fetchServices();
    } else if (activeViewTab === "cheques") {
      fetchCheques();
    } else if (activeViewTab === "installments") {
      fetchInstallments();
    }
  }, [activeViewTab]);

  // Fetch services when invoice type changes (additional safeguard)
  useEffect(() => {
    if (showCreateModal && formData.invoice_type) {
      fetchServices();
    }
  }, [formData.invoice_type]);

  const fetchBasicData = async () => {
    try {
      const [customersRes, taxesRes, cashRes, bankRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/assets/customers`, {
          withCredentials: true,
        }),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/taxes`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/cash`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/assets/current-assets/bank`,
          { withCredentials: true }
        ),
      ]);

      setCustomers(customersRes.data.customers || []);
      setTaxes(taxesRes.data || []);
      setCashAccounts(cashRes.data.cash_accounts || []);
      setBankAccounts(bankRes.data.bank_accounts || []);
    } catch (err) {
      console.error("Failed to fetch basic data:", err);
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError("");

    try {
      const endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services`;
      const response = await axios.get(endpoint, { withCredentials: true });
      setInvoices(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async (invoiceType = null) => {
    try {
      const typeToUse = invoiceType || formData.invoice_type;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/available-services`,
        {
          params: { invoice_type: typeToUse },
          withCredentials: true,
        }
      );
      setServices(response.data.services || []);
    } catch (err) {
      console.error("Failed to fetch services:", err);
    }
  };

  const fetchCheques = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/get-cheques`,
        { withCredentials: true }
      );
      setCheques(response.data || []);
      console.log("Fetched cheques:", response.data);
    } catch (err) {
      console.error("Failed to fetch cheques:", err);
      setError("Failed to fetch cheques");
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallments = async () => {
    try {
      setLoading(true);
      // Since we need installments from all service invoices, we'll need to modify this
      // For now, we'll fetch all service invoices and their installments
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services`,
        { withCredentials: true }
      );

      // Filter invoices that have installments and fetch their installments
      const invoicesWithInstallments = response.data.filter(
        (invoice) =>
          invoice.status === "issued" || invoice.status === "partially_paid"
      );

      const allInstallments = [];
      for (const invoice of invoicesWithInstallments) {
        try {
          const installmentResponse = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/${invoice.id}/installments`,
            { withCredentials: true }
          );
          allInstallments.push(
            ...installmentResponse.data.map((inst) => ({
              ...inst,
              invoice_id: invoice.id,
              invoice_title: invoice.title,
              customer_name: invoice.customer_name,
            }))
          );
        } catch (err) {
          console.error(
            `Failed to fetch installments for invoice ${invoice.id}:`,
            err
          );
        }
      }

      setInstallments(allInstallments);
    } catch (err) {
      console.error("Failed to fetch installments:", err);
      setError("Failed to fetch installments");
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      services_id: "",
      quantity: 1,
      rate: 0,
      description: "",
    };

    setFormData({
      ...formData,
      items: [...(formData.items || []), newItem],
    });
  };

  const handleAddInstallment = () => {
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: [
          ...(formData.payment_info.installments || []),
          {
            due_date: "",
            amount: 0,
            expected_method: "any",
          },
        ],
      },
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...(formData.items || [])];
    updatedItems[index][field] = value;

    // Auto-populate rate based on service selection
    if (field.includes("_id") && value) {
      const service = (services || []).find(
        (s) => s.id.toString() === value.toString()
      );
      if (service) {
        updatedItems[index].rate = service.base_rate;
      }
    }

    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  const handleInstallmentChange = (index, field, value) => {
    const updatedInstallments = [...(formData.payment_info.installments || [])];
    updatedInstallments[index][field] = value;
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: updatedInstallments,
      },
    });
  };

  const removeItem = (index) => {
    const updatedItems = (formData.items || []).filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const removeInstallment = (index) => {
    const updatedInstallments = (
      formData.payment_info.installments || []
    ).filter((_, i) => i !== index);
    setFormData({
      ...formData,
      payment_info: {
        ...formData.payment_info,
        installments: updatedInstallments,
      },
    });
  };

  const calculateSubtotal = () => {
    return (formData.items || []).reduce((sum, item) => {
      return (
        sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)
      );
    }, 0);
  };

  const calculateTaxAmount = () => {
    if (!formData.tax_id) return 0;
    const tax = (taxes || []).find(
      (t) => t.id.toString() === formData.tax_id.toString()
    );
    if (!tax) return 0;

    const baseAmount = calculateSubtotal() - parseFloat(formData.discount || 0);
    return baseAmount * (parseFloat(tax.percentage) / 100);
  };

  const calculateTotalAmount = () => {
    return (
      calculateSubtotal() -
      parseFloat(formData.discount || 0) +
      calculateTaxAmount()
    );
  };

  const calculateTotalPayments = () => {
    let immediateAmount = 0;
    let installmentsTotal = 0;

    if (
      formData.payment_info.immediate_payment &&
      formData.payment_info.amount
    ) {
      immediateAmount = parseFloat(formData.payment_info.amount || 0);
    }

    if (
      formData.payment_info.installments &&
      Array.isArray(formData.payment_info.installments)
    ) {
      installmentsTotal = formData.payment_info.installments.reduce(
        (sum, installment) => sum + parseFloat(installment.amount || 0),
        0
      );
    }

    return immediateAmount + installmentsTotal;
  };

  const validatePaymentAmounts = () => {
    const totalAmount = calculateTotalAmount();
    const totalPayments = calculateTotalPayments();

    // Allow small rounding differences
    const tolerance = 0.01;

    if (Math.abs(totalPayments - totalAmount) > tolerance) {
      return {
        isValid: false,
        message: `Payment amounts (${formatCurrency(
          totalPayments
        )}) do not match invoice total (${formatCurrency(totalAmount)})`,
        difference: totalAmount - totalPayments,
      };
    }

    return { isValid: true, message: "Payment amounts match invoice total" };
  };

  // Cheque management functions
  const handleChequeStatusUpdate = async (chequeId, status, notes = "") => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/cheques/clear`,
        {
          cheque_id: chequeId,
          status,
          notes: notes || null,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        setSuccess(`Cheque ${status} successfully!`);
        fetchCheques();
        fetchInvoices(); // Refresh invoices list to update status
        setChequeStatusModal({
          show: false,
          chequeId: null,
          status: null,
          notes: "",
        });
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update cheque status");
    } finally {
      setLoading(false);
    }
  };

  const openChequeStatusModal = (chequeId, status) => {
    setChequeStatusModal({
      show: true,
      chequeId,
      status,
      notes: "",
    });
  };

  const handleChequeStatusConfirm = () => {
    handleChequeStatusUpdate(
      chequeStatusModal.chequeId,
      chequeStatusModal.status,
      chequeStatusModal.notes
    );
  };

  const openRescheduleModal = (cheque) => {
    setRescheduleModal({
      show: true,
      chequeId: cheque.id,
      oldChequeNumber: cheque.cheque_number,
      newChequeNumber: "",
      newChequeDate: "",
      newBankName: cheque.bank_name || "",
      newBankBranch: cheque.bank_branch || "",
      notes: "",
    });
  };

  const handleRescheduleSubmit = async () => {
    try {
      setLoading(true);

      if (!rescheduleModal.newChequeNumber || !rescheduleModal.newChequeDate) {
        setError("Please fill in the new cheque number and date");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/cheques/reschedule`,
        {
          old_cheque_id: rescheduleModal.chequeId,
          new_cheque_number: rescheduleModal.newChequeNumber,
          new_cheque_date: rescheduleModal.newChequeDate,
          new_bank_name: rescheduleModal.newBankName,
          new_bank_branch: rescheduleModal.newBankBranch,
          notes: rescheduleModal.notes,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        setSuccess("Cheque rescheduled successfully!");
        fetchCheques();
        setRescheduleModal({
          show: false,
          chequeId: null,
          oldChequeNumber: "",
          newChequeNumber: "",
          newChequeDate: "",
          newBankName: "",
          newBankBranch: "",
          notes: "",
        });
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to reschedule cheque");
    } finally {
      setLoading(false);
    }
  };

  // Installment management functions
  const openInstallmentPaymentModal = (installment) => {
    fetchBasicData(); // Ensure we have cash and bank accounts data
    setInstallmentPaymentModal({
      show: true,
      installmentId: installment.id,
      amount: installment.amount,
      paymentInfo: {
        method: "cash",
        asset_type: "cash",
        asset_id: "",
        amount: installment.amount,
        cheque_number: "",
        cheque_date: "",
        bank_name: "",
        bank_branch: "",
        card_transaction_id: "",
        card_bank_name: "",
        card_holder_name: "",
        from_bank_details: "",
        to_bank_details: "",
        transaction_reference: "",
      },
    });
  };

  const handleInstallmentPayment = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services/installments/process`,
        {
          installment_id: installmentPaymentModal.installmentId,
          payment_info: installmentPaymentModal.paymentInfo,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        setSuccess("Installment payment processed successfully!");
        fetchInstallments();
        fetchInvoices(); // Refresh invoices list to update status
        setInstallmentPaymentModal({
          show: false,
          installmentId: null,
          amount: 0,
          paymentInfo: {
            method: "cash",
            asset_type: "cash",
            asset_id: "",
            amount: 0,
            cheque_number: "",
            cheque_date: "",
            bank_name: "",
            bank_branch: "",
            card_transaction_id: "",
            card_bank_name: "",
            card_holder_name: "",
            from_bank_details: "",
            to_bank_details: "",
            transaction_reference: "",
          },
        });
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to process installment payment"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // Validate payment amounts if payments are configured
      if (
        formData.payment_info.immediate_payment ||
        formData.payment_info.installments?.length > 0
      ) {
        const validation = validatePaymentAmounts();
        if (!validation.isValid) {
          setError(validation.message);
          setLoading(false);
          return;
        }
      }

      const endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/invoices/services`;

      const payload = {
        ...formData,
        payment_info: formData.payment_info.immediate_payment
          ? {
              ...formData.payment_info,
              amount: parseFloat(formData.payment_info.amount) || 0,
            }
          : formData.payment_info,
      };

      const response = await axios.post(endpoint, payload, {
        withCredentials: true,
      });

      if (response.status === 201) {
        setSuccess("Services Invoice created successfully!");
        setShowCreateModal(false);
        resetForm();
        fetchInvoices();
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_type: "sale",
      customer_id: "",
      customer_contact_id: "",
      title: "",
      reason: "",
      discount: 0,
      tax_id: "",
      items: [],
      extra_charges: [],
      payment_info: {
        method: "cash",
        asset_type: "cash",
        asset_id: "",
        amount: 0,
        immediate_payment: false,
        installments: [],
        cheque_number: "",
        cheque_date: "",
        bank_name: "",
        bank_branch: "",
        card_transaction_id: "",
        card_bank_name: "",
        card_holder_name: "",
        from_bank_details: "",
        to_bank_details: "",
        transaction_reference: "",
      },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
    }).format(amount || 0);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "partially_paid":
        return "bg-yellow-100 text-yellow-800";
      case "issued":
        return "bg-blue-100 text-blue-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const renderItemForm = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <FaTools className="w-5 h-5 text-purple-600" />
            <span>Services</span>
          </h4>
          <button
            type="button"
            onClick={handleAddItem}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-100 text-purple-700 font-medium rounded-lg hover:bg-purple-200 transition-colors duration-200"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add Item</span>
          </button>
        </div>

        {(formData.items || []).map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-12 gap-4 p-4 bg-gray-50 rounded-lg"
          >
            <div className="col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service
              </label>
              <select
                value={item.services_id || ""}
                onChange={(e) =>
                  handleItemChange(index, "services_id", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Select Service</option>
                {(services || []).map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({formatCurrency(service.base_rate)})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qty
              </label>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  handleItemChange(index, "quantity", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="1"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate
              </label>
              <input
                type="number"
                value={item.rate}
                onChange={(e) =>
                  handleItemChange(index, "rate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total
              </label>
              <input
                type="number"
                value={
                  (parseFloat(item.quantity) || 0) *
                  (parseFloat(item.rate) || 0)
                }
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
            <div className="col-span-2 flex items-end">
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Remove item"
              >
                <FaTrash className="w-4 h-4" />
              </button>
            </div>
            <div className="col-span-12">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={item.description}
                onChange={(e) =>
                  handleItemChange(index, "description", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows="2"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentForm = () => {
    const availableAccounts =
      formData.payment_info.asset_type === "cash" ? cashAccounts : bankAccounts;

    return (
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <FaCreditCard className="w-5 h-5 text-green-600" />
          <span>Payment Information</span>
        </h4>

        {/* Immediate Payment Toggle */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="immediate_payment"
            checked={formData.payment_info.immediate_payment}
            onChange={(e) =>
              setFormData({
                ...formData,
                payment_info: {
                  ...formData.payment_info,
                  immediate_payment: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="immediate_payment"
            className="text-sm font-medium text-gray-700"
          >
            Process immediate payment
          </label>
        </div>

        {/* Payment Details (if immediate payment is selected) */}
        {formData.payment_info.immediate_payment && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                value={formData.payment_info.method}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      method: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Type
              </label>
              <select
                value={formData.payment_info.asset_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      asset_type: e.target.value,
                      asset_id: "",
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cash">Cash Account</option>
                <option value="bank">Bank Account</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account
              </label>
              <select
                value={formData.payment_info.asset_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      asset_id: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select Account</option>
                {(availableAccounts || []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name || account.bank_name} (
                    {formatCurrency(account.amount)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount
              </label>
              <input
                type="number"
                value={formData.payment_info.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_info: {
                      ...formData.payment_info,
                      amount: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
                step="0.01"
                placeholder={`Max: ${formatCurrency(calculateTotalAmount())}`}
              />
            </div>

            {/* Payment Method Specific Fields */}
            {formData.payment_info.method === "cheque" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.cheque_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          cheque_number: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cheque Date
                  </label>
                  <input
                    type="date"
                    value={formData.payment_info.cheque_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          cheque_date: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.bank_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          bank_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Branch
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.bank_branch}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          bank_branch: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {formData.payment_info.method === "card" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_transaction_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_transaction_id: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Bank
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_bank_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_bank_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card Holder Name
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.card_holder_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          card_holder_name: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {formData.payment_info.method === "bank_transfer" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Bank Details
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.from_bank_details}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          from_bank_details: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Bank Details
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.to_bank_details}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          to_bank_details: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    value={formData.payment_info.transaction_reference}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        payment_info: {
                          ...formData.payment_info,
                          transaction_reference: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Installments Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-semibold text-gray-900 flex items-center space-x-2">
              <FaCalendar className="w-4 h-4 text-purple-600" />
              <span>Payment Installments (Optional)</span>
            </h5>
            <button
              type="button"
              onClick={handleAddInstallment}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 font-medium rounded-lg hover:bg-purple-200 transition-colors duration-200"
            >
              <FaPlus className="w-3 h-3" />
              <span>Add Installment</span>
            </button>
          </div>

          {(formData.payment_info.installments || []).map(
            (installment, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-4 p-3 bg-purple-50 rounded-lg"
              >
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={installment.due_date}
                    onChange={(e) =>
                      handleInstallmentChange(index, "due_date", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={installment.amount}
                    onChange={(e) =>
                      handleInstallmentChange(index, "amount", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Method
                  </label>
                  <select
                    value={installment.expected_method}
                    onChange={(e) =>
                      handleInstallmentChange(
                        index,
                        "expected_method",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="any">Any Method</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div className="col-span-2 flex items-end">
                  <button
                    type="button"
                    onClick={() => removeInstallment(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Remove installment"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              Services Invoice
            </h2>
            <p className="mt-2 text-gray-600">
              Create and manage invoices for services
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setActiveViewTab("list")}
              className={`px-6 py-3 font-medium rounded-xl transition-all duration-200 ${
                activeViewTab === "list"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-700 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              View Invoices
            </button>
            <button
              onClick={() => setActiveViewTab("cheques")}
              className={`px-6 py-3 font-medium rounded-xl transition-all duration-200 ${
                activeViewTab === "cheques"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-700 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              <FaCalendar className="w-4 h-4 inline mr-2" />
              Cheques
            </button>
            <button
              onClick={() => setActiveViewTab("installments")}
              className={`px-6 py-3 font-medium rounded-xl transition-all duration-200 ${
                activeViewTab === "installments"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                  : "text-gray-700 bg-gray-100 hover:bg-gray-200"
              }`}
            >
              <FaClock className="w-4 h-4 inline mr-2" />
              Installments
            </button>
            <button
              onClick={() => {
                setActiveViewTab("create");
                setShowCreateModal(true);
              }}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Invoice</span>
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaCheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Success!</span>
            <span className="text-green-700">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaTimes className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">Error!</span>
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {activeViewTab === "list" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {invoices.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaFileInvoiceDollar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No invoices found
              </h3>
              <p className="text-gray-500 mb-6">
                Get started by creating your first services invoice
              </p>
              <button
                onClick={() => {
                  setActiveViewTab("create");
                  setShowCreateModal(true);
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
              >
                <FaPlus className="w-4 h-4" />
                <span>Create Your First Invoice</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(invoices || []).map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{invoice.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            invoice.invoice_type === "sale"
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {invoice.invoice_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.customer_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                            invoice.status
                          )}`}
                        >
                          {invoice.status.replace("_", " ").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            /* viewInvoice(invoice.id) */
                          }}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                          title="View invoice"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cheques Management */}
      {activeViewTab === "cheques" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Service Invoice Cheques</h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage cheques from service invoices
            </p>
          </div>

          {cheques.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCalendar className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No cheques found
              </h3>
              <p className="text-gray-500">
                Cheques from service invoices will appear here for management
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cheque Number
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(cheques || []).map((cheque) => (
                    <tr key={cheque.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {cheque.cheque_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(cheque.cheque_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{cheque.invoice_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cheque.customer_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(cheque.amount_paid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            cheque.status === "cleared"
                              ? "bg-green-100 text-green-800"
                              : cheque.status === "bounced"
                              ? "bg-red-100 text-red-800"
                              : cheque.status === "expired"
                              ? "bg-gray-100 text-gray-800"
                              : cheque.status === "rescheduled"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {cheque.status || "pending"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!cheque.status &&
                          new Date(cheque.cheque_date) <= new Date() && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() =>
                                  openChequeStatusModal(cheque.id, "cleared")
                                }
                                className="text-green-600 hover:text-green-900 p-1 rounded-lg hover:bg-green-50"
                                title="Mark as Cleared"
                              >
                                <FaCheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  openChequeStatusModal(cheque.id, "bounced")
                                }
                                className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50"
                                title="Mark as Bounced"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  openChequeStatusModal(cheque.id, "expired")
                                }
                                className="text-gray-600 hover:text-gray-900 p-1 rounded-lg hover:bg-gray-50"
                                title="Mark as Expired"
                              >
                                <FaClock className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openRescheduleModal(cheque)}
                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded-lg hover:bg-yellow-50"
                                title="Reschedule"
                              >
                                <FaCalendar className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        {new Date(cheque.cheque_date) > new Date() && (
                          <span className="text-gray-400 text-xs">
                            Future date
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Installments Management */}
      {activeViewTab === "installments" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">
              Service Invoice Installments
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage installment payments from service invoices
            </p>
          </div>

          {installments.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaClock className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No installments found
              </h3>
              <p className="text-gray-500">
                Installment payments from service invoices will appear here
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Expected Method
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(installments || []).map((installment) => (
                    <tr key={installment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            #{installment.invoice_id}
                          </div>
                          <div className="text-xs text-gray-500">
                            {installment.invoice_title}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {installment.customer_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(installment.due_date).toLocaleDateString()}
                        {new Date(installment.due_date) < new Date() &&
                          installment.status === "pending" && (
                            <span className="ml-2 text-red-600 text-xs">
                              Overdue
                            </span>
                          )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(installment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {installment.expected_method === "any"
                          ? "Any Method"
                          : installment.expected_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            installment.status === "paid"
                              ? "bg-green-100 text-green-800"
                              : installment.status === "arrival"
                              ? "bg-blue-100 text-blue-800"
                              : installment.status === "rescheduled"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {installment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {installment.status === "pending" && (
                          <button
                            onClick={() =>
                              openInstallmentPaymentModal(installment)
                            }
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-lg hover:bg-blue-50"
                            title="Process Payment"
                          >
                            <FaMoneyBillWave className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Create Services Invoice
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-8">
              {/* Basic Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Type *
                  </label>
                  <select
                    value={formData.invoice_type}
                    onChange={(e) => {
                      const newInvoiceType = e.target.value;
                      setFormData({
                        ...formData,
                        invoice_type: newInvoiceType,
                        items: [], // Clear items when changing invoice type
                      });
                      fetchServices(newInvoiceType); // Pass the new invoice type directly
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="sale">Sale</option>
                    <option value="purchase">Purchase</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) =>
                      setFormData({ ...formData, customer_id: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select Customer (Optional)</option>
                    {(customers || []).map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} ({customer.type})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Invoice title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax
                  </label>
                  <select
                    value={formData.tax_id}
                    onChange={(e) =>
                      setFormData({ ...formData, tax_id: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">No Tax</option>
                    {(taxes || []).map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.name} ({tax.percentage}%)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    rows="3"
                    placeholder="Invoice description or reason"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount
                  </label>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Items */}
              {renderItemForm()}

              {/* Payment Information */}
              {renderPaymentForm()}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <FaCalculator className="w-5 h-5 text-blue-600" />
                  <span>Invoice Summary</span>
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Subtotal</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculateSubtotal())}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Discount</p>
                    <p className="text-lg font-semibold text-red-600">
                      -{formatCurrency(formData.discount)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Tax</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(calculateTaxAmount())}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatCurrency(calculateTotalAmount())}
                    </p>
                  </div>
                </div>

                {/* Payment Summary */}
                {(formData.payment_info.immediate_payment ||
                  formData.payment_info.installments.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {formData.payment_info.immediate_payment && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            Immediate Payment
                          </p>
                          <p className="text-lg font-semibold text-green-600">
                            {formatCurrency(formData.payment_info.amount)}
                          </p>
                        </div>
                      )}
                      {formData.payment_info.installments.length > 0 && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            Installments Total
                          </p>
                          <p className="text-lg font-semibold text-purple-600">
                            {formatCurrency(
                              (formData.payment_info.installments || []).reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              )
                            )}
                          </p>
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-sm text-gray-600">
                          Remaining Balance
                        </p>
                        <p
                          className={`text-lg font-semibold ${
                            calculateTotalAmount() -
                              (parseFloat(formData.payment_info.amount) || 0) -
                              (formData.payment_info.installments || []).reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              ) >
                            0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {formatCurrency(
                            calculateTotalAmount() -
                              (parseFloat(formData.payment_info.amount) || 0) -
                              (formData.payment_info.installments || []).reduce(
                                (sum, inst) =>
                                  sum + (parseFloat(inst.amount) || 0),
                                0
                              )
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Payment Validation Status */}
                    {(formData.payment_info.immediate_payment ||
                      formData.payment_info.installments.length > 0) && (
                      <div className="mt-4 text-center">
                        <div
                          className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                            validatePaymentAmounts().isValid
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                          }`}
                        >
                          <FaCheckCircle className="w-4 h-4 mr-2" />
                          {validatePaymentAmounts().message}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-6 py-3 text-gray-700 bg-gray-100 font-medium rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    (formData.items || []).length === 0 ||
                    ((formData.payment_info.immediate_payment ||
                      formData.payment_info.installments?.length > 0) &&
                      !validatePaymentAmounts().isValid)
                  }
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <FaSave className="w-4 h-4" />
                  )}
                  <span>{loading ? "Creating..." : "Create Invoice"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cheque Status Modal */}
      {chequeStatusModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {chequeStatusModal.status === "cleared"
                ? "Clear"
                : chequeStatusModal.status === "bounced"
                ? "Mark as Bounced"
                : chequeStatusModal.status === "expired"
                ? "Mark as Expired"
                : "Update Status"}{" "}
              Cheque
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={chequeStatusModal.notes}
                onChange={(e) =>
                  setChequeStatusModal({
                    ...chequeStatusModal,
                    notes: e.target.value,
                  })
                }
                placeholder="Add any notes about this status update..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="3"
                maxLength="500"
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {chequeStatusModal.notes.length}/500 characters
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleChequeStatusConfirm}
                disabled={loading}
                className={`flex-1 text-white px-4 py-2 rounded-lg font-medium transition-colors ${
                  chequeStatusModal.status === "cleared"
                    ? "bg-green-600 hover:bg-green-700"
                    : chequeStatusModal.status === "bounced"
                    ? "bg-red-600 hover:bg-red-700"
                    : chequeStatusModal.status === "expired"
                    ? "bg-gray-600 hover:bg-gray-700"
                    : "bg-blue-600 hover:bg-blue-700"
                } disabled:opacity-50`}
              >
                {loading ? "Updating..." : "Confirm"}
              </button>
              <button
                onClick={() =>
                  setChequeStatusModal({
                    show: false,
                    chequeId: null,
                    status: null,
                    notes: "",
                  })
                }
                disabled={loading}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cheque Reschedule Modal */}
      {rescheduleModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-4">Reschedule Cheque</h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700">
                Current Cheque
              </h4>
              <p className="text-sm text-gray-600">
                Cheque Number: {rescheduleModal.oldChequeNumber}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Cheque Number *
                </label>
                <input
                  type="text"
                  value={rescheduleModal.newChequeNumber}
                  onChange={(e) =>
                    setRescheduleModal({
                      ...rescheduleModal,
                      newChequeNumber: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new cheque number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Cheque Date *
                </label>
                <input
                  type="date"
                  value={rescheduleModal.newChequeDate}
                  onChange={(e) =>
                    setRescheduleModal({
                      ...rescheduleModal,
                      newChequeDate: e.target.value,
                    })
                  }
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={rescheduleModal.newBankName}
                  onChange={(e) =>
                    setRescheduleModal({
                      ...rescheduleModal,
                      newBankName: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bank name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Branch
                </label>
                <input
                  type="text"
                  value={rescheduleModal.newBankBranch}
                  onChange={(e) =>
                    setRescheduleModal({
                      ...rescheduleModal,
                      newBankBranch: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bank branch"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Rescheduling
                </label>
                <textarea
                  value={rescheduleModal.notes}
                  onChange={(e) =>
                    setRescheduleModal({
                      ...rescheduleModal,
                      notes: e.target.value,
                    })
                  }
                  placeholder="Explain why the cheque is being rescheduled..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                  maxLength="500"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {rescheduleModal.notes.length}/500 characters
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleRescheduleSubmit}
                disabled={
                  loading ||
                  !rescheduleModal.newChequeNumber ||
                  !rescheduleModal.newChequeDate
                }
                className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Rescheduling..." : "Reschedule Cheque"}
              </button>
              <button
                onClick={() =>
                  setRescheduleModal({
                    show: false,
                    chequeId: null,
                    oldChequeNumber: "",
                    newChequeNumber: "",
                    newChequeDate: "",
                    newBankName: "",
                    newBankBranch: "",
                    notes: "",
                  })
                }
                disabled={loading}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Installment Payment Modal */}
      {installmentPaymentModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Process Installment Payment
            </h3>

            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700">
                Installment Details
              </h4>
              <p className="text-sm text-gray-600">
                Amount: {formatCurrency(installmentPaymentModal.amount)}
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={installmentPaymentModal.paymentInfo.method}
                    onChange={(e) =>
                      setInstallmentPaymentModal({
                        ...installmentPaymentModal,
                        paymentInfo: {
                          ...installmentPaymentModal.paymentInfo,
                          method: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Asset Type
                  </label>
                  <select
                    value={installmentPaymentModal.paymentInfo.asset_type}
                    onChange={(e) =>
                      setInstallmentPaymentModal({
                        ...installmentPaymentModal,
                        paymentInfo: {
                          ...installmentPaymentModal.paymentInfo,
                          asset_type: e.target.value,
                          asset_id: "",
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="cash">Cash Account</option>
                    <option value="bank">Bank Account</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account
                  </label>
                  <select
                    value={installmentPaymentModal.paymentInfo.asset_id}
                    onChange={(e) =>
                      setInstallmentPaymentModal({
                        ...installmentPaymentModal,
                        paymentInfo: {
                          ...installmentPaymentModal.paymentInfo,
                          asset_id: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Account</option>
                    {(installmentPaymentModal.paymentInfo.asset_type === "cash"
                      ? cashAccounts
                      : bankAccounts
                    ).map((account) => (
                      <option key={account.id} value={account.id}>
                        {installmentPaymentModal.paymentInfo.asset_type ===
                        "cash"
                          ? account.name
                          : `${account.bank_name} - ${account.account_number}`}
                        ({formatCurrency(account.amount)})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={installmentPaymentModal.paymentInfo.amount}
                    onChange={(e) =>
                      setInstallmentPaymentModal({
                        ...installmentPaymentModal,
                        paymentInfo: {
                          ...installmentPaymentModal.paymentInfo,
                          amount: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              {/* Payment method specific fields */}
              {installmentPaymentModal.paymentInfo.method === "cheque" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cheque Number
                    </label>
                    <input
                      type="text"
                      value={installmentPaymentModal.paymentInfo.cheque_number}
                      onChange={(e) =>
                        setInstallmentPaymentModal({
                          ...installmentPaymentModal,
                          paymentInfo: {
                            ...installmentPaymentModal.paymentInfo,
                            cheque_number: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cheque Date
                    </label>
                    <input
                      type="date"
                      value={installmentPaymentModal.paymentInfo.cheque_date}
                      onChange={(e) =>
                        setInstallmentPaymentModal({
                          ...installmentPaymentModal,
                          paymentInfo: {
                            ...installmentPaymentModal.paymentInfo,
                            cheque_date: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={installmentPaymentModal.paymentInfo.bank_name}
                      onChange={(e) =>
                        setInstallmentPaymentModal({
                          ...installmentPaymentModal,
                          paymentInfo: {
                            ...installmentPaymentModal.paymentInfo,
                            bank_name: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Branch
                    </label>
                    <input
                      type="text"
                      value={installmentPaymentModal.paymentInfo.bank_branch}
                      onChange={(e) =>
                        setInstallmentPaymentModal({
                          ...installmentPaymentModal,
                          paymentInfo: {
                            ...installmentPaymentModal.paymentInfo,
                            bank_branch: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Similar sections for card and bank_transfer can be added here if needed */}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleInstallmentPayment}
                disabled={
                  loading ||
                  !installmentPaymentModal.paymentInfo.asset_id ||
                  !installmentPaymentModal.paymentInfo.amount
                }
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Process Payment"}
              </button>
              <button
                onClick={() =>
                  setInstallmentPaymentModal({
                    show: false,
                    installmentId: null,
                    amount: 0,
                    paymentInfo: {
                      method: "cash",
                      asset_type: "cash",
                      asset_id: "",
                      amount: 0,
                      cheque_number: "",
                      cheque_date: "",
                      bank_name: "",
                      bank_branch: "",
                      card_transaction_id: "",
                      card_bank_name: "",
                      card_holder_name: "",
                      from_bank_details: "",
                      to_bank_details: "",
                      transaction_reference: "",
                    },
                  })
                }
                disabled={loading}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesInvoice;
